/* OsPlanDetailsViewRenderer.js */

"use strict";
const _ = require("lodash");

const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const DistinctValuesFeed = require("../../../../lib/DistinctValuesFeed");
const readOnlyFieldIds = ["status", "imageAssociations", "deviceCount", "lastWorkId", "osmOptions"];
const RunNowComponent = require("./veils/RunNowComponent");
const RemoveInvalidDevicesComponent = require("./veils/RemoveInvalidDevicesComponent");
const ScheduleComponent = require("./veils/ScheduleComponent");
const OsmOptionsComponent = require("./veils/OsmOptionsComponent");
const childViewComponent = require("../../../../widgets/childview/ChildViewRenderer");
const manageDeviceComponent = require("./subviews/manage_device/ManageDeviceViewComponent");
const validateViewComponent = require("./subviews/validate/ValidateViewComponent");
const StatusFlow = require("./StatusFlowComponent");
const disabledStatus = ["RUNNING", "COMPLETE", "CANCELED", "ERROR", "VALIDATING", "SCHEDULED"];
const disabledStatusForACS = ["RUNNING", "COMPLETE", "CANCELED", "ERROR", "VALIDATING"];
class OsPlanDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("os-plan-details-view-gl");
        this._rootElem.component = this.model;
        this._logger = this.model.logger;
        this._isDirty = false;
        this._formsComponent = new FormsComponent();
        this._childViewRenderer = undefined;
        this.flowGraphic = undefined;
        this._previousStatus = undefined;
        this._flowGraphicContainer = document.createElement("div");
        this._flowGraphicContainer.className = "status-flow-graphic";

        this._gridSection = document.createElement("div");
        this._groupingValuesFeed = new DistinctValuesFeed({
            startFeed: (fieldName) => {
                // Initiating the query empty here since the startRelatedDeviceDistinctValuesFeed
                // does not need any query as it is contextual to the plan
                const distinctQuery = {$userQuery: {}};
                return this._model._plan.startRelatedDeviceDistinctValuesFeed(
                    fieldName,
                    distinctQuery
                );
            },
        });
        this._emptyMessage = document.createElement("game-editor-message-gl");
        this._emptyMessage.classList.add("actionable");
        this._iconContainer = document.createElement("span");
        this._iconContainer.classList.add("icon-container");
        this._icon = document.createElement("game-editor-actionable-icon-gl");
        this._icon.dataset.icon = "configdrift.add_device";
        this._iconContainer.classList.add("has-icon");
        this._iconContainer.appendChild(this._icon);
        this._messageContainer = document.createElement("span");
        this._messageContainer.classList.add("message-container");
        this._messageContainer.innerHTML = "Add a Device";
        this._emptyMessage.appendChild(this._iconContainer);
        this._emptyMessage.appendChild(this._messageContainer);
        this._emptyDeviceContainer = document.createElement("game-editor-message-gl");
        this._emptyDeviceContainer.classList.add("empty-support-msg");
        this._emptyDeviceContainer.innerHTML = "No Supported Devices Available";
        this._validateView = undefined;
        this._runNowComponent = undefined;
        this._renderingForm = false;
        this._actionSelection = undefined;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._model.on(
            "PlanPropertiesChanged",
            (this._onPlanPropertiesChanged = _.debounce(
                this._onPlanPropertiesChanged.bind(this),
                500,
                false
            ))
        );
        this._model.on(
            "RefreshPlanDetails",
            (this._onRefreshPlanDetails = this._onRefreshPlanDetails.bind(this))
        );
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(
                this._onFormsPendingPatchesChanged.bind(this),
                500,
                false
            ))
        );
        this._model._pagingRelatedFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        this._model._pagingDeviceFeed.on(
            "RefreshView",
            (this._onFeedDeviceRefreshView = this._onFeedDeviceRefreshView.bind(this))
        );
        if (this._groupingValuesFeed) {
            this._groupingValuesFeed.on(
                "ValueDeleted",
                (this._onGroupValuesChanged = this._onGroupValuesChanged.bind(this))
            );
            this._groupingValuesFeed.on(
                "ValueAdded",
                (this._onGroupValuesChanged = this._onGroupValuesChanged.bind(this))
            );
        }
        this._iconContainer.addEventListener(
            "click",
            (this._onAddDevices = _.debounce(_.bind(this._onAddDevices, this), 3000, {
                maxWait: 3000,
                leading: true,
                trailing: false,
            }))
        );
        this._messageContainer.addEventListener(
            "click",
            (this._onAddDevices = _.debounce(_.bind(this._onAddDevices, this), 3000, {
                maxWait: 3000,
                leading: true,
                trailing: false,
            }))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener("PlanPropertiesChanged", this._onPlanPropertiesChanged);
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
        this._model.removeListener("RefreshPlanDetails", this._onRefreshPlanDetails);
        this._model._pagingRelatedFeed.removeListener("RefreshView", this._onFeedRefreshView);
        this._model._pagingDeviceFeed.removeListener("RefreshView", this._onFeedDeviceRefreshView);
        if (this._groupingValuesFeed) {
            this._groupingValuesFeed.removeListener("ValueDeleted", this._onGroupValuesChanged);
            this._groupingValuesFeed.removeListener("ValueAdded", this._onGroupValuesChanged);
        }
        if (this._planResultsGrid && this._onGridUserSorted) {
            this._planResultsGrid.removeListener("UserSorted", this._onGridUserSorted);
        }
        this._iconContainer.removeEventListener("click", this._onAddDevices);
        this._messageContainer.removeEventListener("click", this._onAddDevices);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._onFormsPendingPatchesChanged.flush();
        let promArr = [
            this._formsComponent.shutdown(),
            this.flowGraphic.shutdown(),
            this._childViewRenderer && this._childViewRenderer.shutdown(),
            this._planResultsGrid ? this._planResultsGrid.shutdown() : Promise.resolve(),
            this._validateView ? this._validateView.shutdown() : Promise.resolve(),
        ];

        if (this._model._modalUserFeedback.isDisplayed()) {
            promArr.push(this._model._modalUserFeedback.hideDisplayed());
        }

        return Promise.all(promArr).then(
            () => {
                this._gridSection.remove();
                this._gridSection = undefined;
                this._childViewRenderer = undefined;
                this._formsComponent = undefined;
                this._renderingForm = undefined;
                this._isDirty = undefined;
                this._logger = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._planResultsGrid = undefined;
                this._groupingValuesFeed = undefined;
                this._iconContainer = undefined;
                this._messageContainer = undefined;
                this._emptyMessage.remove();
                this._emptyMessage = undefined;
                this._validateView = undefined;
                this._runNowComponent = undefined;
                this._emptyDeviceContainer = undefined;
                this.flowGraphic = undefined;
                this._flowGraphicContainer = undefined;
                this._previousStatus = undefined;
                this._actionSelection = undefined;
                Promise.resolve();
            },
            (err) => {
                if (!_.isEmpty(this._rootElem)) {
                    if (this._rootElem.remove) {
                        this._rootElem.remove();
                    }
                    this._rootElem = undefined;
                }
                this._logger.warn({error: err}, "OsPlanDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */

    render() {
        this.rootElement.innerHTML = "";
        // this.rootElement.appendChild(this._flowGraphicContainer);
        this.rootElement.appendChild(this._formsComponent.renderer.rootElement);
        this.rootElement.appendChild(this._gridSection);
        return this.model.whenModelIsReady().then(
            () => {
                this._renderPlanDetails().then(() => {
                    this._renderGrid();
                    this._createActions();
                    this._showViewActions();
                });
                this._registerListeners();
            },
            (err) => err
        );
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _createActions() {
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SavePlanDetails",
            handler: (this._onPlanDetailsSave = this._onPlanDetailsSave.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelPlanDetails",
            handler: (this._onPlanDetailsCancel = this._onPlanDetailsCancel.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Configure Options",
            name: "OsmOptions",
            handler: (this._onOsmOptionsClick = this._onOsmOptionsClick.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Remove Invalid Devices",
            name: "RemoveInvalidDevices",
            handler: (this._onRemoveInvalidDevices = this._onRemoveInvalidDevices.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Manage Devices",
            name: "ManageDevices",
            handler: (this._onManageDevices = _.debounce(this._onManageDevices.bind(this), 300)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Validate Plan",
            name: "ValidatePlan",
            handler: (this._onValidatePlan = this._onValidatePlan.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Validation Logs",
            name: "ValidationLogs",
            handler: (this._onViewValidationLogs = this._onViewValidationLogs.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Schedule",
            name: "SchedulePlan",
            handler: (this._onSchedulePlan = this._onSchedulePlan.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel Schedule",
            name: "CancelSchedulePlan",
            handler: (this._onCancelSchedulePlan = this._onCancelSchedulePlan.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Run Now",
            name: "RunNowPlan",
            handler: (this._onRunNowPlan = this._onRunNowPlan.bind(this)),
        });
        this.model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Reset",
            name: "ResetPlan",
            handler: (this._onResetPlan = this._onResetPlan.bind(this)),
        });
    }

    _enableDisableOsmOptions() {
        if (this._model._osmOptionEntities && this._model._osmOptionEntities.length > 0) {
            this.model.gameEditor.enableViewAction("OsmOptions");
        } else {
            this.model.gameEditor.disableViewAction("OsmOptions");
        }
    }

    _showViewActions(passedStatus) {
        let status = _.isEmpty(passedStatus) ? this._model._plan.status : passedStatus;
        this._showAllActions();
        this._enableAllActions();
        this._showHideSaveAction(status);
        this._showHideValidateAction(status);
        this._showHideScheduleAction(status);
        this._showHideValidationLogsAction();
        this._showHideResetPlanAction();
        this._showHideRemoveInvalidDevicesAction(status);
        this._enableDisableOsmOptions();
    }

    _showHideResetPlanAction() {
        if (this._model._planCanBeReset) {
            this.model.gameEditor.showViewAction("ResetPlan");
        } else {
            this.model.gameEditor.hideViewAction("ResetPlan");
        }
    }

    _showHideValidationLogsAction() {
        let lastWorkId =
            _.get(this.model._plan.getPristineProperties(), "lastWorkId") ||
            this._model._validateWorkId;
        let planStatus = this.model._plan.getStatus();
        if (_.isUndefined(this._model.childViewOpen)) {
            if (lastWorkId && planStatus !== "DRAFT") {
                this.model.gameEditor.showViewAction("ValidationLogs");
                this.model.gameEditor.hideViewAction("ValidatePlan");
            } else {
                this.model.gameEditor.showViewAction("ValidatePlan");
                this.model.gameEditor.hideViewAction("ValidationLogs");
            }
        }
    }

    _showAllActions() {
        this.model.gameEditor.showViewAction("SavePlanDetails");
        this.model.gameEditor.showViewAction("CancelPlanDetails");
        this.model.gameEditor.showViewAction("ManageDevices");
        this.model.gameEditor.showViewAction("ValidatePlan");
        this.model.gameEditor.showViewAction("RunNowPlan");
        this.model.gameEditor.showViewAction("SchedulePlan");
        this.model.gameEditor.showViewAction("CancelSchedulePlan");
        this.model.gameEditor.showViewAction("RemoveInvalidDevices");
        this.model.gameEditor.showViewAction("OsmOptions");
    }

    _enableAllActions() {
        this.model.gameEditor.enableViewAction("SavePlanDetails");
        this.model.gameEditor.enableViewAction("CancelPlanDetails");
        this.model.gameEditor.enableViewAction("ManageDevices");
        this.model.gameEditor.enableViewAction("ValidatePlan");
        this.model.gameEditor.enableViewAction("RunNowPlan");
        this.model.gameEditor.enableViewAction("SchedulePlan");
        this.model.gameEditor.enableViewAction("CancelSchedulePlan");
        this.model.gameEditor.enableViewAction("RemoveInvalidDevices");
    }

    _disableAllViewActions() {
        this.model.gameEditor.disableViewAction("SavePlanDetails");
        this.model.gameEditor.disableViewAction("CancelPlanDetails");
        this.model.gameEditor.disableViewAction("ManageDevices");
        this.model.gameEditor.disableViewAction("ValidatePlan");
        this.model.gameEditor.disableViewAction("RunNowPlan");
        this.model.gameEditor.disableViewAction("SchedulePlan");
        this.model.gameEditor.disableViewAction("CancelSchedulePlan");
        this.model.gameEditor.disableViewAction("RemoveInvalidDevices");
    }

    _showHideSaveAction(status) {
        if (
            _.includes(["DRAFT", "INVALID", "VALIDATION_ERROR", "VALIDATION_CANCELED"], status) ||
            (status === "VALIDATED" && status !== "SCHEDULED")
        ) {
            this._toggleActions();
        } else {
            this.model.gameEditor.disableViewAction("SavePlanDetails");
            this.model.gameEditor.disableViewAction("CancelPlanDetails");
            this.model.gameEditor.disableViewAction("ManageDevices");
        }
    }

    _showHideValidateAction(status) {
        switch (status) {
            case "DRAFT":
                this._enableAllActions();
                this.model.gameEditor.disableViewAction("RunNowPlan");
                this.model.gameEditor.disableViewAction("SchedulePlan");
                if (!this.model.isDirty()) {
                    this._disableViewActions();
                }
                this._setViewActionText("ValidatePlan", "Validate Plan");
                if (this._validateView) {
                    this._validateView._renderer._enableValidatePlan();
                    this._validateView._renderer._disableStopValidation();
                    this._validateView._renderer._disableDownload();
                    this._setViewActionText("ValidateViewPlan", "Validate Plan");
                }
                break;
            case "INVALID":
            case "VALIDATION_ERROR":
            case "VALIDATION_CANCELED":
                this._enableAllActions();
                this.model.gameEditor.disableViewAction("RunNowPlan");
                this.model.gameEditor.disableViewAction("SchedulePlan");
                if (!this.model.isDirty()) {
                    this._disableViewActions();
                }
                this._setViewActionText("ValidatePlan", "Validate Plan");
                if (this._validateView) {
                    this._validateView._renderer._enableValidatePlan();
                    this._validateView._renderer._disableStopValidation();
                    this._validateView._renderer._enableDownload();
                    this._setViewActionText("ValidateViewPlan", "Validate Plan");
                }
                break;
            case "VALIDATED":
                this._enableAllActions();
                if (!this.model.isDirty()) {
                    this._disableViewActions();
                }
                this._setViewActionText("ValidatePlan", "Validate Plan");

                if (
                    this._validateView ||
                    _.get(this._childViewRenderer, "_view.parentData.childViewOpen", false)
                ) {
                    this._setViewActionText("ValidateViewPlan", "Validate Plan");
                    this._validateView._renderer._enableValidatePlan();
                    this._validateView._renderer._disableStopValidation();
                    this._validateView._renderer._enableDownload();
                } else {
                    this.model.gameEditor.hideViewAction("CancelSchedulePlan");
                    this.model.gameEditor.showViewAction("SchedulePlan");
                }
                break;
            case "VALIDATING":
                this._setViewActionText("ValidatePlan", "Validating...");
                if (this._validateView) {
                    this._validateView._renderer._disableValidatePlan();
                    this._validateView._renderer._enableStopValidation();
                    this._validateView._renderer._disableDownload();
                    this._setViewActionText("ValidateViewPlan", "Validating...");
                }
                this._disableAllViewActions();
                break;
            case "QUEUED":
            case "RUNNING":
            case "COMPLETE":
            case "ERROR":
            case "CANCELED":
                this._disableAllViewActions();
                if (this._validateView) {
                    this._validateView._renderer._disableAllValidateAction();
                }
                break;
            case "SCHEDULED":
                this.model.gameEditor.disableViewAction("ValidatePlan");
                this.model.gameEditor.showViewAction("CancelSchedulePlan");
                this.model.gameEditor.hideViewAction("SchedulePlan");
                if (this._validateView) {
                    this._validateView._renderer._disableValidatePlan();
                    this._validateView._renderer._disableStopValidation();
                    this._validateView._renderer._enableDownload();
                }
                break;
        }
    }

    _showHideScheduleAction(status) {
        if (status === "SCHEDULED") {
            this.model.gameEditor.disableViewAction("RunNowPlan");
            this.model.gameEditor.hideViewAction("SchedulePlan");
            this.model.gameEditor.showViewAction("CancelSchedulePlan");
        } else if (status === "VALIDATED") {
            this.model.gameEditor.enableViewAction("RunNowPlan");
            this.model.gameEditor.hideViewAction("CancelSchedulePlan");
        } else {
            this.model.gameEditor.disableViewAction("SchedulePlan");
            this.model.gameEditor.disableViewAction("RunNowPlan");
            this.model.gameEditor.hideViewAction("CancelSchedulePlan");
        }
    }

    _enableDisableRunNowAction(status) {
        if (status === "RUNNING" || status === "QUEUED") {
            this._disableAllViewActions();
            this._setViewActionText("RunNowPlan", "Running...");
        } else if (status === "VALIDATED" && status !== "SCHEDULED") {
            this.model.gameEditor.enableViewAction("RunNowPlan");
        } else if (status === "VALIDATED" && status === "SCHEDULED") {
            this.model.gameEditor.disableViewAction("RunNowPlan");
        } else if (
            (status === "DRAFT" ||
                status === "VALIDATION_ERROR" ||
                status === "VALIDATION_CANCELED") &&
            this._isDirty === false
        ) {
            this.model.gameEditor.disableViewAction("RunNowPlan");
        }
    }

    _showHideRemoveInvalidDevicesAction(status) {
        if (status === "INVALID" && this._model.plan.deviceUnhealthyCount > 0) {
            this.model.gameEditor.showViewAction("RemoveInvalidDevices");
        } else {
            this.model.gameEditor.hideViewAction("RemoveInvalidDevices");
        }
    }

    _hideViewActions() {
        this.model.gameEditor.hideViewAction("SavePlanDetails");
        this.model.gameEditor.hideViewAction("CancelPlanDetails");
        this.model.gameEditor.hideViewAction("ManageDevices");
        this.model.gameEditor.hideViewAction("ValidatePlan");
        this.model.gameEditor.hideViewAction("ValidationLogs");
        this.model.gameEditor.hideViewAction("SchedulePlan");
        this.model.gameEditor.hideViewAction("RunNowPlan");
        this.model.gameEditor.hideViewAction("CancelSchedulePlan");
        this.model.gameEditor.hideViewAction("ResetPlan");
        this.model.gameEditor.hideViewAction("RemoveInvalidDevices");
        this.model.gameEditor.hideViewAction("OsmOptions");
    }

    _disableViewActions() {
        this.model.gameEditor.disableViewAction("SavePlanDetails");
        this.model.gameEditor.disableViewAction("CancelPlanDetails");
    }

    _enableViewActions() {
        this.model.gameEditor.enableViewAction("SavePlanDetails");
        this.model.gameEditor.enableViewAction("CancelPlanDetails");
    }

    _toggleActions() {
        if (this._model.isDirty()) {
            this._enableViewActions();
        } else {
            this._disableViewActions();
        }
    }

    _hideLeftFooterContainer() {
        let leftSelector = document.querySelector("left-container");
        leftSelector.style.display = "none";
    }

    _showLeftFooterContainer() {
        let querySelector = document.querySelector("left-container");
        querySelector.style.display = "block";
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderPlanDetails() {
        return Promise.resolve().then(
            () => {
                let jtype = this._getJtype();
                let pristineValue = this._getPristineValue();
                let updatedPristineValueStatus = this._updatePristineValueStatus(pristineValue);
                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(updatedPristineValueStatus, true);

                this._renderingForm = true;

                return this._formsComponent.renderer.render().then(() => {
                    this._renderingForm = false;
                    const statusFormEl = this._formsComponent.renderer._rootElem.querySelector(
                        "forms-gl div.rendered_control.graphic"
                    );
                    if (statusFormEl.contains(this._flowGraphicContainer)) {
                        statusFormEl.removeChild(this._flowGraphicContainer);
                    }
                    this._flowGraphicContainer = document.createElement("div");
                    this._flowGraphicContainer.className = "status-flow-graphic";
                    this.flowGraphic = undefined;
                    this.flowGraphic = new StatusFlow();
                    this._flowGraphicContainer.appendChild(this.flowGraphic.renderer.rootElement);
                    statusFormEl.insertBefore(
                        this._flowGraphicContainer,
                        statusFormEl.querySelector("input-text-control")
                    );
                    this._setInitialFlowGraphicStatus(pristineValue.status);
                });
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "OsPlanDetailsViewRenderer._renderPlanDetails() error."
                );
            }
        );
    }

    _renderGrid() {
        this._gridSection.classList.add("grid-section");
        this._gridSection.innerHTML = "";
        let leftContainer = this.model.gameEditorRootElement.querySelector(
            "game-editor-view-actions-gl left-container"
        );
        leftContainer.innerHTML = "";
        this._planResultsGrid = new EntityGrid({
            gridKey: this._model._gridKey,
            columns: this._model.columnConfig,
            pagingFeed: this._model._pagingRelatedFeed,
            selectable: false,
            showHeader: true,
            showLinesNumber: this.model.gameEditorRootElement.querySelector(
                "game-editor-view-actions-gl left-container"
            ),
            modalUserFeedback: this._model._modalUserFeedback,
            maxGroups: null, //consider what will happen if we set value to maxGroups
            groupingValuesFeed: this._groupingValuesFeed,
        });
        this._planResultsGrid.on(
            "UserSorted",
            (this._onGridUserSorted = this._onGridUserSorted.bind(this))
        );
        this._refreshGrid();
        this._renderGridRow();
    }

    _renderGridRow() {
        let groupBy = _.get(
            this._formsComponent.getValue(),
            "groupedBy",
            _.get(this._getPristineValue(), "groupedBy", "detectedSku")
        );
        if (this._planResultsGrid) {
            this._planResultsGrid.renderer.renderPromise
                .then(() => {
                    if (groupBy === "detectedSku") {
                        if (this._model.gameEditor._expandOpt) {
                            return this._planResultsGrid.setGroupBy(
                                "detectedSku",
                                this._model.gameEditor._expandOpt
                            );
                        } else if (this._planResultsGrid.groupedBy !== "detectedSku") {
                            return this._planResultsGrid.setGroupBy("detectedSku");
                        }
                    } else {
                        return this._model.gameEditor._expandOpt
                            ? this._planResultsGrid.setGroupBy(
                                  undefined,
                                  this._model.gameEditor._expandOpt
                              )
                            : this._planResultsGrid.setGroupBy();
                    }
                })
                .then(() => {
                    this._gridSection.appendChild(this._planResultsGrid.renderer.rootElement);
                });
        }
    }

    _setViewActionText(buttonValue, buttonText) {
        let buttonControl = _.get(
            this.model.gameEditor._viewContainerElem.nextElementSibling.lastElementChild.querySelectorAll(
                "button-control[value='" + buttonValue + "']"
            ),
            0
        );

        if (buttonControl) {
            buttonControl.innerHTML = buttonText;
        }
    }

    _updatePristineValueStatus(pristineValues) {
        let updatedPristineValues = _.clone(pristineValues);
        let statusKey = pristineValues.status;
        updatedPristineValues.status = _.get(
            this._model._gameEditor._osPlanStatusList,
            statusKey,
            statusKey
        );
        return updatedPristineValues;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _refreshGrid() {
        this._model._pagingRelatedFeed._planFeedReestablish();
    }

    _onGridUserSorted(sortData) {
        this._model.onUserSorted(sortData);
    }

    _onFormsPendingPatchesChanged() {
        if (!_.isEmpty(this._model)) {
            let isImageAssociated = this._model._plan.imageAssociations;
            const form = this._formsComponent.getValue();
            if (
                this._model.groupBy !== form.groupedBy &&
                isImageAssociated &&
                !_.isEmpty(isImageAssociated)
            ) {
                this._model
                    ._clearImageAssociationVeil(form.groupedBy)
                    .then(() => {
                        this._onFormChangeValue();
                    })
                    .catch((error) => {
                        if (error.userRejected) {
                            form.groupedBy = _.get(this._model._plan, "groupedBy", "detectedSku");
                            let updatedPristineValueStatus = this._updatePristineValueStatus(form);
                            this._formsComponent.setPristineValue(updatedPristineValueStatus);
                            return Promise.resolve();
                        } else {
                            console.warn(error);
                        }
                    });
            } else {
                this._onFormChangeValue();
            }
        }
    }

    _onFormChangeValue() {
        if (!_.isEmpty(this._model)) {
            const form = this._formsComponent.getValue();

            if (this._model.groupBy !== form.groupedBy) {
                this._model.groupBy = form.groupedBy;
                this._renderGridRow();
            }
            if (!this._renderingForm) {
                const formName = form.name;
                this._model._plan.setName(formName);
                this._model._plan.setDescription(form.description);
                this._model._plan.setAction(form.action);
                this._model._plan.setSkipCiscoApiImageChecks(form.imgValidationArea.imgValidation);

                this._model._plan.setGroupedBy(form.groupedBy);

                /* Checkboxes for ACS Setting - Start */
                if (!_.includes(disabledStatusForACS, this._model._plan.getStatus())) {
                    // const checkboxPost = this._formsComponent.getRenderer().rootElement.querySelectorAll('.snapshot_type fieldset .array_container .array_checkbox')[1];
                    const checkboxPost = this._formsComponent
                        .getRenderer()
                        .rootElement.querySelectorAll(
                            ".snapshot_type > .form_entry .array_container .array_checkbox"
                        )[1];
                    const checkboxPostInputCtl =
                        checkboxPost.querySelector("input-checkbox-control");
                    if (this._actionSelection !== form.action) {
                        // Do this action only when the action is changed
                        this._actionSelection = form.action;

                        if (
                            (this._model.isOsUpgradeAutoCaptured && form.action === "DEPLOY") ||
                            (this._model.isOsTransferAutoCaptured && form.action === "TRANSFER")
                        ) {
                            if (!checkboxPostInputCtl.classList.contains("checked")) {
                                checkboxPostInputCtl.click();
                            }
                            checkboxPostInputCtl.setAttribute("disabled", true);
                        } else {
                            if (
                                _.isEmpty(
                                    _.get(
                                        this._model._plan.getPristineProperties(),
                                        "snapshotType",
                                        ""
                                    )
                                ) ||
                                _.isEmpty(
                                    _.get(
                                        this._model._plan.getPristineProperties(),
                                        "snapshotType",
                                        ""
                                    )
                                        .split("_")
                                        .find("POST")
                                )
                            ) {
                                if (checkboxPostInputCtl.classList.contains("checked")) {
                                    checkboxPostInputCtl.click();
                                }
                            }
                            checkboxPostInputCtl.removeAttribute("disabled");
                        }
                    }

                    const checkboxList = this._formsComponent
                        .getRenderer()
                        .rootElement.querySelectorAll(
                            ".snapshot_type .form_entry .array_container .array_checkbox"
                        )[2];
                    const checkboxListInputCtl =
                        checkboxList.querySelector("input-checkbox-control");
                    if (!checkboxPostInputCtl.classList.contains("checked")) {
                        if (checkboxListInputCtl.classList.contains("checked")) {
                            checkboxListInputCtl.click();
                        }
                        checkboxListInputCtl.setAttribute("disabled", true);
                    } else {
                        checkboxListInputCtl.removeAttribute("disabled");
                    }
                    this._model._plan.setSnapshotType(this._getSnapshotString(form.snapshotType));
                }
                /* Checkboxes for ACS Setting - Start */
            }
        }
        return Promise.resolve();
    }

    _getSnapshotString(input) {
        const sortArray = ["PRE", "POST", "DEF"],
            result = [];

        sortArray.forEach((key) => {
            let found = false;
            input = input.filter((item) => {
                if (!found && item === key) {
                    result.push(item);
                    found = true;
                    return false;
                } else {
                    return true;
                }
            });
        });

        return result.length > 0 ? result.join("_") : "NONE";
    }

    _onPlanPropertiesChanged(pristineProperties, before) {
        if (!this._model) {
            return;
        }

        if (_.includes(disabledStatus, pristineProperties.status)) {
            this._rootElem.classList.add("disabled-form");
        } else {
            this._rootElem.classList.remove("disabled-form");
        }

        this._model.groupBy = _.get(pristineProperties, "groupedBy", "detectedSku");
        this._setFlowGraphicStatus(pristineProperties.status);
        // this._setFlowGraphicStatus(pristineProperties.status);
        // Toggling the button on all prop changes, though redundant reduces risk
        this._showHideRemoveInvalidDevicesAction(pristineProperties.status);
        if (_.get(pristineProperties, "status", null) !== _.get(before, "status", null)) {
            switch (pristineProperties.status) {
                case "DRAFT":
                    if (!this._model.isDirty()) {
                        this._disableViewActions();
                    } else {
                        this._enableViewActions();
                    }
                    this._enableDisableRunNowAction(pristineProperties.status);
                    this._showHideValidationLogsAction();
                    // this._showHideRemoveInvalidDevicesAction(pristineProperties.status);
                    this.model.gameEditor.disableViewAction("SchedulePlan");
                    break;
                case "INVALID":
                case "VALIDATING":
                case "VALIDATION_ERROR":
                case "VALIDATION_CANCELED":
                    this._showHideValidateAction(pristineProperties.status);
                    // this._showHideRemoveInvalidDevicesAction(pristineProperties.status);
                    break;
                case "VALIDATED":
                    this._showHideValidateAction(pristineProperties.status);
                    // this._showHideRemoveInvalidDevicesAction(pristineProperties.status);
                    // Checking if Remove Invalid Devices veil opened before re-rendering form
                    // otherwise the veil is closed before has summary displayed
                    this._renderPlanDetails();
                    this._renderGrid();
                    break;
                case "QUEUED":
                case "RUNNING":
                    this._enableDisableRunNowAction(pristineProperties.status);
                    break;
                case "COMPLETE":
                    this._disableAllViewActions();
                    this._setViewActionText("RunNowPlan", "Run Now");
                    this._showHideResetPlanAction();
                    break;
                case "CANCELED":
                case "ERROR":
                    this._disableAllViewActions();
                    this._setViewActionText("RunNowPlan", "Run Now");
                    break;
                case "SCHEDULED":
                    this._onPlanCancelsActions();
                    this._renderPlanDetails();
                    this.model.gameEditor.showViewAction("CancelSchedulePlan");
                    this.model.gameEditor.hideViewAction("SchedulePlan");
                    break;
            }
        }

        if (!this._renderingForm) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let pristinePropsVal = this._getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE
            let isDirty = this.model.isDirty();

            let updateReadOnlyFields = () => {
                _.keys(pristinePropsVal).map((id) => {
                    if (readOnlyFieldIds.indexOf(id) === -1) {
                        pristinePropsVal[id] = formVal[id];
                    }
                });
                let updatedPristineValueStatus = this._updatePristineValueStatus(pristinePropsVal);
                this._formsComponent.setPristineValue(updatedPristineValueStatus);
                this._renderGridRow();
                return Promise.resolve();
            };

            if (isDirty) {
                let isReadOnlyUpdate = true;
                _.keys(pristineProperties).map((id) => {
                    if (
                        readOnlyFieldIds.indexOf(id) === -1 &&
                        !_.isEqual(
                            _.isEmpty(pristineProperties[id]) ? "" : pristineProperties[id],
                            _.isEmpty(before[id]) ? "" : before[id]
                        )
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });
                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This plan has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onPlanDetailsRefreshAction =
                            this._onPlanDetailsRefreshAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                let updatedPristineValueStatus = this._updatePristineValueStatus(pristinePropsVal);
                this._formsComponent.setPristineValue(updatedPristineValueStatus);
                this._renderGridRow();
                return Promise.resolve();
            }
        }
    }

    _setFlowGraphicStatus(status) {
        // if (status !== "DRAFT" && status === this._previousStatus) {
        //     return;
        // }
        this._previousStatus = status;
        switch (status) {
            case "DRAFT":
                if (!this._model.isDirty()) {
                    const pristineProps = this._model._plan.getPristineProperties();
                    if (pristineProps.status !== "DRAFT") {
                        this._setInitialFlowGraphicStatus(pristineProps.status);
                    } else {
                        this.flowGraphic.setState("Draft", "saved");
                        this.flowGraphic.setState("Validate", "default");
                        this.flowGraphic.setState("Run", "default");
                    }
                } else {
                    this.flowGraphic.resetAll();
                }
                break;
            case "VALIDATION_ERROR":
                this.flowGraphic.setState("Validate", "error");
                break;
            case "VALIDATION_CANCELED":
                this.flowGraphic.setState("Validate", "canceled");
                break;
            case "INVALID":
                this.flowGraphic.setState("Draft", "saved");
                this.flowGraphic.setState("Validate", "error");
                break;
            case "VALIDATING":
                this.flowGraphic.setState("Validate", "validating");
                break;
            case "QUEUED":
            case "VALIDATED":
                this.flowGraphic.setState("Validate", "validated");
                break;
            case "RUNNING":
                this.flowGraphic.setState("Run", "running");
                break;
            case "COMPLETE":
                this.flowGraphic.setState("Run", "success");
                break;
            case "CANCELED":
                this.flowGraphic.setState("Run", "cancelled");
                break;
            case "ERROR":
                this.flowGraphic.setState("Run", "error");
                break;
            case "SCHEDULED":
                this.flowGraphic.setState("Run", "scheduled");
                break;
        }
    }

    _setInitialFlowGraphicStatus(status) {
        this.flowGraphic.setState("Draft", "saved");
        if (status === "DRAFT") {
            return;
        }
        switch (status) {
            case "VALIDATION_ERROR":
                this.flowGraphic.setState("Validate", "error");
                break;
            case "VALIDATION_CANCELED":
                this.flowGraphic.setState("Validate", "canceled");
                break;
            case "INVALID":
                this.flowGraphic.setState("Validate", "error");
                break;
            case "VALIDATING":
                this.flowGraphic.setState("Validate", "validating");
                break;
            case "QUEUED":
            case "VALIDATED":
                this.flowGraphic.setState("Validate", "validated");
                break;
            case "RUNNING":
                this.flowGraphic.setState("Validate", "validated");
                this.flowGraphic.setState("Run", "running");
                break;
            case "COMPLETE":
                this.flowGraphic.setState("Validate", "validated");
                this.flowGraphic.setState("Run", "success");
                break;
            case "CANCELED":
                this.flowGraphic.setState("Validate", "validated");
                this.flowGraphic.setState("Run", "cancelled");
                break;
            case "ERROR":
                this.flowGraphic.setState("Validate", "validated");
                this.flowGraphic.setState("Run", "error");
                break;
            case "SCHEDULED":
                this.flowGraphic.setState("Validate", "validated");
                this.flowGraphic.setState("Run", "scheduled");
                break;
        }
    }

    _onFeedRefreshView() {
        let totalEntities = _.get(this._model._pagingRelatedFeed, "totalEntities", 0);
        let groupingValueLength = _.get(this._groupingValuesFeed, "_valuesList", []);
        this._showGridMessage(totalEntities, groupingValueLength);
    }

    _onRefreshPlanDetails() {
        this._onPlanDetailsRefreshAction();
    }

    _onFeedDeviceRefreshView() {
        if (!this._model) {
            return;
        }
        let totalEntities = _.get(this._model._pagingRelatedFeed, "totalEntities", 0);
        let groupingValueLength = _.get(this._groupingValuesFeed, "_valuesList", []);
        if (totalEntities === 0 && groupingValueLength.length === 0) {
            this._showDefaultMessageDisplay();
        }
    }

    _onGroupValuesChanged() {
        let totalEntities = _.get(
            this._model && this._model._pagingRelatedFeed,
            "_pagingRelatedFeed",
            0
        );
        let groupingValueLength = _.get(this._groupingValuesFeed, "_valuesList", []);
        this._showGridMessage(totalEntities, groupingValueLength);
    }

    _showGridMessage(totalFeed, totalGroupFeed) {
        if (totalFeed === 0 && totalGroupFeed.length === 0) {
            this._showDefaultMessageDisplay();
        } else {
            this._hideDefaultMessageDisplay();
        }
    }

    _showDefaultMessageDisplay() {
        if (this._gridSection) {
            this._gridSection.innerHTML = "";
            let totalEntities = _.get(this._model._pagingDeviceFeed, "totalEntities", 0);
            if (totalEntities !== 0) {
                this._gridSection.appendChild(this._emptyMessage);
            } else {
                this._gridSection.appendChild(this._emptyDeviceContainer);
            }
        }
    }

    _hideDefaultMessageDisplay() {
        this._emptyMessage.remove();
    }

    _onDirty(dirtyState) {
        this._isDirty = dirtyState;
        this._toggleActions();
        this._setFlowGraphicStatus(this._model._plan.getStatus());
    }

    _onPlanDetailsSave() {
        this._disableViewActions();
        return this._model
            .savePlan()
            .then(() => {
                this._disableViewActions();
            })
            .catch((/*err*/) => {
                this._enableViewActions();
            });
    }

    _onOsmOptionsClick() {
        return this._model._modalUserFeedback.displayCustom(new OsmOptionsComponent(this._model), {
            dontHide: false,
        });
    }

    _onPlanDetailsCancel() {
        this.model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onPlanDetailsRefreshAction = this._onPlanDetailsRefreshAction.bind(this))
        );
    }

    _onPlanDetailsRefreshAction() {
        this.model.resetPlanProperties();
        this._refreshGrid();
        this._model._gameEditor.clearViewActions();
        return this.render();
    }

    _onPlanCancelsActions(passedStatus) {
        this._showViewActions(passedStatus);
    }

    _onAddDevices() {
        this._onManageDevices();
    }

    _onManageDevices() {
        let manageDevice = new manageDeviceComponent(this._model);
        let whenLoaderShown = this._model._modalUserFeedback.displayLoading({
            message: "Loading devices...",
        });
        return manageDevice.renderer
            .render()
            .then(() => {
                this._childViewRenderer = new childViewComponent({
                    data: manageDevice.renderer.rootElement,
                    backLink: "Details",
                    title: "Manage Device",
                    childData: manageDevice,
                    parentData: this._model,
                    backButton: true,
                });
            })
            .then(() => whenLoaderShown)
            .then(() => this._model._modalUserFeedback.hideDisplayed())
            .catch((err) => {
                // Loading the plan's devices can fail on large plans. Report it
                // rather than opening a view whose device lists would be wrong.
                this._model.childViewOpen = undefined;
                this._logger.warn(
                    {error: err},
                    "OsPlanDetailsViewRenderer._onManageDevices() error."
                );
                // The child registered its toolbar actions and started its feeds before
                // failing: tear it down, then report the original error.
                return manageDevice
                    .shutdown()
                    .catch((teardownErr) => {
                        this._logger.warn(
                            {error: teardownErr},
                            "OsPlanDetailsViewRenderer._onManageDevices() teardown error."
                        );
                    })
                    .then(() => whenLoaderShown)
                    .then(() => this._model._modalUserFeedback.displayError(err));
            });
    }

    _onValidatePlan() {
        this._validateView = new validateViewComponent(this._model);

        const validatePromise = [
            this._model._plan.isDirty() ? this._model.savePlan() : Promise.resolve(),
        ];

        Promise.all(validatePromise)
            .then(() => {
                //this._setViewActionText("ValidatePlan", "Validating...");
                this._model._gameEditor.hideViewAction("ValidatePlan");
                this._model._gameEditor.showViewAction("ValidationLogs");
                return this._validateView.renderer.render();
            })
            .then(() => {
                this._setViewActionText("ValidateViewPlan", "Validating...");
                this._validateView._renderer._disableDownload();
                this._validateView._renderer._disableValidatePlan();
                this._childViewRenderer = new childViewComponent({
                    data: this._validateView.renderer.rootElement,
                    backLink: "Details",
                    title: "OS Plan Validation",
                    childData: this._validateView,
                    parentData: this._model,
                    backButton: true,
                });
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OsPlanDetailsViewRenderer._onValidatePlan() error."
                );
            });
    }

    _onSchedulePlan() {
        return this._checkDirty()
            .then(() => {
                return this._onPlanScheduleUserAction();
            })
            .catch((err) => {
                if (err.userRejected) {
                    return Promise.resolve(this._renderPlanDetails())
                        .then(() => this._renderGrid())
                        .then(() => {
                            this._isDirty = false;
                            return this._model._gameEditor.getTabCmpnt().setDirty(false);
                        })
                        .then(() => this._model._modalUserFeedback.hideDisplayed())
                        .then(() => this._onPlanScheduleUserAction());
                } else {
                    console.error("Error in _onSchedulePlan()! Err:", err);
                }
            });
    }

    _checkDirty() {
        if (this._isDirty) {
            return this._model._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this._onPlanDetailsSave();
                })
                .catch(() => {
                    return Promise.resolve();
                });
        } else {
            return Promise.resolve();
        }
    }

    _onPlanScheduleUserAction() {
        let promAll = this._discardPlanChanges();
        return Promise.all(promAll)
            .then(() => {
                return this._model._modalUserFeedback.displayCustom(
                    new ScheduleComponent(this._model),
                    {dontHide: false}
                );
            })
            .then((/*result*/) => {
                this._onPlanCancelsActions("SCHEDULED");
                return this._refreshGrid();
            })
            .then(() => {
                this._renderPlanDetails();
                return this._renderGrid();
            })
            .then(() => {
                this._model._modalUserFeedback.hideDisplayed();
            })
            .catch((err) => {
                if (err && err.userRejected) {
                    this._logger.warn(
                        {error: err},
                        "OsPlanDetailsViewRenderer._onSchedulePlan() - User Canceled"
                    );
                    return;
                } else {
                    this._logger.warn(
                        {error: err},
                        "OsPlanDetailsViewRenderer._onSchedulePlan() - Error"
                    );
                    return this._model._modalUserFeedback.displayError(err);
                }
            });
    }

    _discardPlanChanges() {
        if (this._isDirty) {
            return [
                this.model.resetPlanProperties(),
                this._refreshGrid(),
                this._model._gameEditor.clearViewActions(),
                this.render(),
            ];
        } else {
            return [];
        }
    }

    _onRunNowPlan() {
        return this._checkDirty()
            .then(() => {
                return this._onRunNow();
            })
            .catch((err) => {
                if (err.userRejected) {
                    return Promise.resolve(this._renderPlanDetails())
                        .then(() => this._renderGrid())
                        .then(() => {
                            this._isDirty = false;
                            return this._model._gameEditor.getTabCmpnt().setDirty(false);
                        })
                        .then(() => this._model._modalUserFeedback.hideDisplayed());
                } else {
                    console.error("Error in _onSchedulePlan()! Err:", err);
                }
            });
    }

    _onRunNow() {
        let promAll = this._discardPlanChanges();
        return Promise.all(promAll)
            .then(() => {
                return this._model._modalUserFeedback.displayCustom(
                    new RunNowComponent(this._model),
                    {dontHide: false}
                );
            })
            .catch((err) => {
                if (err && err.userRejected) {
                    this._logger.warn(
                        {error: err},
                        "OsPlanDetailsViewRenderer._onRunNow() - User Canceled"
                    );
                    return;
                } else {
                    this._logger.warn(
                        {error: err},
                        "OsPlanDetailsViewRenderer._onRunNow() - Error"
                    );
                    return this._model._modalUserFeedback.displayError(err);
                }
            });
    }

    _onCancelSchedulePlan() {
        return this._model._gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel the scheduled run?",
                title: "Cancel Schedule",
                confirmDetails: {
                    text: "Confirm",
                    type: "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
            },
            () => {
                return this._model
                    ._cancelScheduler()
                    .then((/*workResults*/) => {
                        this._enableDisableRunNowAction();
                        this._enableAllActions();
                        this._toggleActions();
                        this.model.gameEditor.hideViewAction("CancelSchedulePlan");
                        this.model.gameEditor.showViewAction("SchedulePlan");
                        return this._refreshGrid();
                    })
                    .then(() => {
                        this._renderPlanDetails();
                        this._renderGrid();
                    })
                    .catch((err) => {
                        if (err && err.userRejected) {
                            this._logger.warn(
                                {error: err},
                                "OsPlanDetailsViewRenderer._onCancelSchedulePlan() - User Canceled"
                            );
                            return;
                        } else {
                            this._logger.warn(
                                {error: err},
                                "OsPlanDetailsViewRenderer._onCancelSchedulePlan() - Error"
                            );
                            if (this._model._plan.status !== "SCHEDULED") {
                                this._enableDisableRunNowAction();
                                this._enableAllActions();
                                this._toggleActions();
                                this.model.gameEditor.hideViewAction("CancelSchedulePlan");
                                this.model.gameEditor.showViewAction("SchedulePlan");
                                this._refreshGrid().then(() => {
                                    this._renderPlanDetails();
                                    this._renderGrid();
                                });
                            }

                            return this._model._modalUserFeedback.displayError(err);
                        }
                    });
            }
        );
    }

    _onViewValidationLogs() {
        this._model._modalUserFeedback.displayLoading();
        let validateViewRenderer;
        if (!this._validateView) {
            this._validateView = new validateViewComponent(this._model);
            validateViewRenderer = this._validateView._renderer;
        }
        this._validateView._workId =
            this.model._plan.getPristineProperties().lastWorkId || this._model._validateWorkId;
        this._validateView._getValidationLogs(this._validateView._workId).then(() => {
            this._childViewRenderer = new childViewComponent({
                data: this._validateView.renderer.rootElement,
                backLink: "Details",
                title: "OS Plan Validation",
                childData: this._validateView,
                parentData: this._model,
                backButton: true,
            });
            validateViewRenderer._registerViewActions();
        });
    }

    _onResetPlan() {
        //Todo: reset plan to draft
        return this._model._modalUserFeedback
            .displayConfirmation("The status of the plan will be reset to DRAFT.", {
                title: "Reset OS Plan Status",
                confirmDetails: {
                    text: "OK",
                    type: "text",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
            })
            .then(() => {
                this._model._plan.setStatus("DRAFT");
                return this._onPlanDetailsSave();
            })
            .then(() => {
                this._model._planCanBeReset = false;
                return this._onPlanDetailsRefreshAction();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "OS Plan::Plan reset failed");
                return Promise.resolve();
            });
    }

    _onRemoveInvalidDevices() {
        return this._model._modalUserFeedback
            .displayCustom(new RemoveInvalidDevicesComponent(this._model), {dontHide: false})
            .catch((err) => {
                if (err && err.userRejected) {
                    this._logger.warn(
                        {error: err},
                        "OsPlanDetailsViewRenderer._onRemoveInvalidDevices() - User Canceled"
                    );
                    return;
                } else {
                    this._logger.warn(
                        {error: err},
                        "OsPlanDetailsViewRenderer._onRemoveInvalidDevices() - Error"
                    );
                    return this._model._modalUserFeedback.displayError(err);
                }
            });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getPristineValue() {
        let pristineProperties = this._model._plan.getPristineProperties();
        let pristineValue = {};

        if (pristineProperties) {
            pristineValue.name = pristineProperties.name;
            pristineValue.description = pristineProperties.description;
            pristineValue.status = pristineProperties.status;
            pristineValue.action = _.get(pristineProperties, "action", "TRANSFER");
            pristineValue.groupedBy = _.get(pristineProperties, "groupedBy", "detectedSku");
            pristineValue.snapshotType =
                _.get(pristineProperties, "snapshotType", "") === "NONE"
                    ? []
                    : _.get(pristineProperties, "snapshotType", "").split("_");
            pristineValue.imgValidationArea = {};
            pristineValue.imgValidationArea.imgValidation = _.get(
                pristineProperties,
                "skipCiscoApiImageChecks",
                false
            );
        }
        return pristineValue;
    }

    _getJtype() {
        let jtype = {};

        jtype.name = "";
        jtype["name@Display.label"] = "Name";

        jtype.graphic = ""; // Placeholder for flow graphic display
        jtype["graphic@Display.label"] = "Progress";

        jtype.description = "";
        jtype["description@Display.label"] = "Description";

        jtype.status = "";
        jtype["status@Display.label"] = "Status";
        jtype["status@Display.readOnly"] = true;

        jtype.action = "TRANSFER";
        jtype["action@Display.label"] = "Plan Action";
        jtype["action@Enum"] = ["TRANSFER", "DEPLOY"];
        jtype["action@Display.enum"] = ["Transfer Image Only", "Deploy New Image"];
        jtype["action@Required!"] = true;

        jtype.snapshotType = ["PRE", "POST", "DEF"];
        jtype["snapshotType@Display.label"] = "Config Snapshot";
        jtype["snapshotType@Display.checkList"] = {
            labels: ["Capture before", "Capture after", "Set as default"],
            showAdd: false,
            showDelete: false,
            showReOrder: false,
            allowEdit: false,
            multiSelect: false,
            showCheckAll: false,
            showUnCheckAll: false,
        };

        // ["NONE", "PRE", "POST", "POST_DEF", "PRE_POST", "PRE_POST_DEF"]
        // jtype.snapshotType = "NONE";
        // jtype["snapshotType@Display.label"] = "Config Snapshot";

        // jtype["snapshotType@Enum"] = ["NONE", "PRE", "POST", "POST_DEF", "PRE_POST", "PRE_POST_DEF"];
        // jtype["snapshotType@Display.enum"] = [
        //     "None",
        //     "Capture Pre-OS Plan Snapshot",
        //     "Capture Post-OS Plan Snapshot",
        //     "Capture Post-OS Plan Snapshot and mark as Default",
        //     "Capture both Pre and Post-OS Plan Snapshots",
        //     "Capture both Pre and Post-OS Plan Snapshots, mark Post as Default"
        // ];
        // jtype["snapshotType@Required!"] = true;

        jtype.groupedBy = "detectedSku";
        jtype["groupedBy@Display.label"] = "Group By";
        jtype["groupedBy@Enum"] = ["detectedSku", "None"];
        jtype["groupedBy@Display.enum"] = ["SKU", "None"];
        jtype["groupedBy@Required!"] = true;
        jtype.imgValidationArea = {};
        jtype["imgValidationArea@Display.label"] = "Validation";
        const area = jtype.imgValidationArea;
        area.imgValidation = false;
        area["imgValidation@Display.label"] = "Skip API image compatibility check";
        if (!this._model._isCiscoEnabled || !this._model._isVendorApiImageCheckEnabled) {
            jtype["imgValidationArea@Display.styles"] = {
                cssStyle: {
                    display: "none",
                },
            };
        }

        if (!this._model._userHasosmImageValidationOverride) {
            jtype["imgValidationArea@Display.styles"] = {
                cssStyle: {
                    display: "none",
                },
            };
            // jtype["imgValidationArea@Display.readOnly"] = true;
            // area["imgValidation@Display.readOnly"] = true;
        }

        if (_.includes(disabledStatus, this._model._plan.status)) {
            this._rootElem.classList.add("disabled-form");
            // jtype["name@Display.readOnly"] = true;
            // jtype["description@Display.readOnly"] = true;
            // jtype["action@Display.readOnly"] = true;
            // jtype["groupedBy@Display.readOnly"] = true;
            // jtype["snapshotType@Display.readOnly"] = true;
        } else {
            this._rootElem.classList.remove("disabled-form");
            // jtype["name@Display.readOnly"] = false;
            // jtype["description@Display.readOnly"] = false;
            // jtype["action@Display.readOnly"] = false;
            // jtype["groupedBy@Display.readOnly"] = false;
            // jtype["snapshotType@Display.readOnly"] = false;
        }
        return jtype;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = OsPlanDetailsViewRenderer;
